import { memo, useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { IcStatusPin } from "@racoon/icons";
import { Popover, PopoverContent, PopoverTrigger, cn } from "@racoon/ui";
import { STATUS_TONE_CLASS, type StatusTone } from "@racoon/lib";

import { BUCKETS, BucketKey } from "~/shared/lib/pickup-status/pickup-status.lib";
import { PickupStatusFilters, usePickupStatus } from "~/shared/lib/pickup-status/use-pickup-status";
import { useDelayedFlag } from "~/shared/hooks/use-delayed-flag";
import { useHeldWhileLoading } from "~/shared/hooks/use-held-while-loading";
import { summarizeFleetOnLine } from "~/shared/lib/pickup-status/fleet-on-line";
import { FleetOnLineSection } from "./fleet-on-line.section";

const BUCKET_TONE: Record<BucketKey, StatusTone> = {
  unassigned: "pending",
  planned: "assigned",
  done: "done",
  missed: "failed",
};
const BUCKET_CLASS = Object.fromEntries(
  BUCKETS.map((b) => {
    const { bg, color } = STATUS_TONE_CLASS[BUCKET_TONE[b]];
    return [b, `${bg} ${color}`];
  }),
) as Record<BucketKey, string>;

const PIN_CLASS: Record<BucketKey, string> = {
  unassigned: "text-tone-pending",
  planned: "text-blue-300",
  done: "text-tone-done",
  missed: "text-tone-failed",
};
// The blocks put НЕВЫВОЗ before ВЫПОЛНЕНО; the bars keep BUCKETS order.
const BLOCK_ORDER: BucketKey[] = ["unassigned", "planned", "missed", "done"];

// Below this share a segment is too narrow for its label.
const MIN_LABEL_PCT = 6;

// Per the design: "1228.4" in every language, a dot and no digit grouping.
const fmt = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  useGrouping: false,
});

type LabelKey =
  | BucketKey
  | "bySites"
  | "byVolume"
  | "sites"
  | "volume"
  | "weight"
  | "m3"
  | "tonnes"
  | "loadError";

function pct(value: number, total: number) {
  return total > 0 ? (value / total) * 100 : 0;
}

export type FleetSource = {
  trucks: ConnectProto.Truck[];
  /** Shifts on the day: their trucks are the ones on the line. */
  dayShifts: ConnectProto.DriverOnShift[];
  /** Also the overnight ones, so the day's work resolves to its truck. */
  allShifts: ConnectProto.DriverOnShift[];
};

function PickupStatusPanel({
  fleet,
  fleetError = false,
  ...filters
}: PickupStatusFilters & { fleet?: FleetSource; fleetError?: boolean }) {
  const t = useT();
  const k = (key: LabelKey) => t(Namespace.Routes, `pickupStatusTab.${key}`);
  const { summary, jobs, routes, isFetching, isError } = usePickupStatus(filters);
  // The jobs are the previous day's while a new day loads, so the row is computed only from
  // settled data and the last one stays up meanwhile, together with the totals it shares out.
  const freshFleetOnLine = useMemo(
    () =>
      fleet && !isFetching
        ? {
            fleet: summarizeFleetOnLine({ ...fleet, jobs, routes }),
            volume: summary.done.volume,
            weightT: summary.doneWeightT,
          }
        : undefined,
    [fleet, isFetching, jobs, routes, summary.done.volume, summary.doneWeightT],
  );
  // Same scope as the jobs' own hold: another org, division or category starts empty.
  const fleetOnLine = useHeldWhileLoading(
    freshFleetOnLine,
    `${filters.organizationId}|${filters.divisionId}|${filters.category}`,
    isError || fleetError,
  );
  const dimmed = useDelayedFlag(isFetching);
  const fleetDimmed = useDelayedFlag(!freshFleetOnLine && !!fleetOnLine);

  if (isError) {
    return <div className="p-6 text-sm text-text-error">{k("loadError")}</div>;
  }

  const m3 = k("m3");
  const bucketLabels = Object.fromEntries(BUCKETS.map((b) => [b, k(b)])) as Record<
    BucketKey,
    string
  >;

  return (
    // A size container: numbers and gaps scale with the card (cqw), which narrows when the side panel opens.
    <div className={cn("transition-opacity [container-type:inline-size]", dimmed && "opacity-50")}>
      <div className="flex">
        <div className="flex w-[clamp(200px,29.3%,394px)] shrink-0 flex-col gap-6 border-r p-6">
          <StackedBar
            title={k("bySites")}
            labels={bucketLabels}
            values={BUCKETS.map((b) => summary[b].sites)}
            format={(v) => fmt.format(v)}
          />
          <StackedBar
            title={k("byVolume")}
            labels={bucketLabels}
            values={BUCKETS.map((b) => summary[b].volume)}
            format={(v) => `${fmt.format(v)} ${m3}`}
          />
        </div>
        {BLOCK_ORDER.map((b) => (
          <div
            key={b}
            className="flex min-w-0 flex-initial flex-col gap-4 border-r px-[clamp(12px,1.8cqw,24px)] py-6 last:flex-auto last:border-r-0"
          >
            <h3
              title={k(b)}
              className="flex h-6 min-w-0 items-center gap-1 whitespace-nowrap text-xs font-bold uppercase text-text-body"
            >
              <IcStatusPin aria-hidden className={cn("size-6 shrink-0", PIN_CLASS[b])} />
              <span className="truncate">{k(b)}</span>
            </h3>
            <div className="flex flex-wrap gap-x-[clamp(12px,1.8cqw,24px)] gap-y-3">
              <Metric
                label={k("sites")}
                badge={`${fmt.format(pct(summary[b].sites, summary.total.sites))}%`}
                badgeClass={BUCKET_CLASS[b]}
                value={fmt.format(summary[b].sites)}
              />
              <Metric
                label={k("volume")}
                badge={`${fmt.format(pct(summary[b].volume, summary.total.volume))}%`}
                badgeClass={BUCKET_CLASS[b]}
                value={`${fmt.format(summary[b].volume)} ${m3}`}
              />
              {b === "done" && (
                <Metric
                  label={k("weight")}
                  value={`${fmt.format(summary.doneWeightT)} ${k("tonnes")}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {fleetOnLine && fleetOnLine.fleet.types.length > 0 && (
        // The panel already dims while the jobs load; this covers the fleet arriving after them.
        <div className={cn("transition-opacity", fleetDimmed && !dimmed && "opacity-50")}>
          <FleetOnLineSection
            fleet={fleetOnLine.fleet}
            volume={fleetOnLine.volume}
            weightT={fleetOnLine.weightT}
            fmt={fmt}
          />
        </div>
      )}
    </div>
  );
}

function StackedBar({
  title,
  labels,
  values,
  format,
}: {
  title: string;
  labels: Record<BucketKey, string>;
  values: number[];
  format: (v: number) => string;
}) {
  const total = values.reduce((a, b) => a + b, 0);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-bold uppercase text-text-body">{title}</span>
      <div className="flex h-6 overflow-hidden rounded bg-gray-100 text-xs font-medium tracking-[-0.01em]">
        {BUCKETS.map((b, i) => {
          const share = pct(values[i], total);
          if (share === 0) return null;
          // A click opens the figures: a narrow segment has no room for its label, and a
          // hover title takes too long to appear.
          return (
            <Popover key={b}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  style={{ width: `${share}%` }}
                  aria-label={`${labels[b]}: ${format(values[i])}`}
                  className={cn(
                    "flex items-center justify-center truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-surface-action",
                    BUCKET_CLASS[b],
                  )}
                >
                  {share >= MIN_LABEL_PCT && format(values[i])}
                </button>
              </PopoverTrigger>
              <PopoverContent
                width="custom"
                side="top"
                className="w-auto whitespace-nowrap px-3 py-2 text-xs font-medium text-text-body"
              >
                {labels[b]}: {format(values[i])} · {fmt.format(share)}%
              </PopoverContent>
            </Popover>
          );
        })}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  badge,
  badgeClass,
}: {
  label: string;
  value: string;
  badge?: string;
  badgeClass?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-text-subtle">{label}</span>
      {badge !== undefined ? (
        <span
          className={cn(
            "flex h-5 w-fit items-center rounded-full px-2 text-xs font-bold",
            badgeClass,
          )}
        >
          {badge}
        </span>
      ) : (
        <span className="h-5" />
      )}
      <span className="whitespace-nowrap text-[clamp(17px,1.65cqw,22px)] font-bold leading-7 tracking-[-0.005em] text-text-body">
        {value}
      </span>
    </div>
  );
}

export default memo(PickupStatusPanel);
